import {
    AnalyticsContext,
    initialAnalyticsContext,
} from "@/Contexts/AnalyticsContext";
import { CookieManagerContext } from "@/Contexts/CookieManagerContext";
import { Client, Coach, PageProps } from "@/types/page";
import { User } from "@/types/user";
import { getCookie } from "@/utils/CookieJar";
import { usePageProps } from "@/utils/dataLayer";
import * as amplitude from "@amplitude/analytics-browser";
import { Experiment } from "@amplitude/experiment-js-client";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { usePage } from "@inertiajs/react";
import { PropsWithChildren, useContext, useEffect, useState } from "react";

/**
 * Cookie used by automated tests (Playwright / Appium) to default every
 * unhandled experiment to a specific variant (typically "control").
 */
const E2E_FORCE_DEFAULT_VARIANT_COOKIE = "e2e_force_default_variant";

export function AnalyticsProvider({ children }: PropsWithChildren) {
    const { isImpersonating, impersonatorId } = usePage<PageProps>().props;
    const cookieSettings = useContext(CookieManagerContext).cookieSettings;
    const experimentation = Experiment.initializeWithAmplitudeAnalytics(
        import.meta.env.VITE_AMPLITUDE_API_KEY,
        {
            automaticExposureTracking: false,
        },
    );
    const [experimentationInitialized, setExperimentationInitialized] =
        useState(false);

    const optOut = () => {
        // remove amplitude cookies (start with AMP_)
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
            if (cookies[i].trim().indexOf("AMP_") === 0) {
                const cookieName = cookies[i].split("=")[0];
                document.cookie =
                    cookieName +
                    "=fu; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=." +
                    window.location.hostname;
            }
        }

        amplitude.setOptOut(true);
    };

    const trackEvent = (
        eventName: string,
        eventProperties?: Record<
            string,
            string | number | boolean | object | null
        >,
    ) => {
        if (!isAnalyticsAllowed()) {
            return;
        }
        amplitude.track(eventName, {
            ...eventProperties,
            is_impersonating: isImpersonating,
            impersonator_id: impersonatorId,
            is_mobile_app: !!window.isMobileApp,
        });
    };

    /**
     * This function allows us to ensure an event is always attached to a client in amplitude even if the user who emits the event is a coach.
     */
    const trackEventAttachedToClient = (
        currentUser: User,
        clientId: number,
        eventName: string,
        eventProperties?: Record<string, string | number | boolean | object>,
    ) => {
        if (currentUser.type === "client") {
            if (currentUser.id !== clientId) {
                throw new Error(
                    "Client cannot track events on behalf of another client",
                );
            }
            trackEvent(eventName, eventProperties);
            return;
        }
        window.axios.post(
            route("logOnBehalfOf", {
                client: clientId,
            }),
            {
                eventName,
                eventProperties,
            },
        );
    };

    const identifyUser = (user: Client | Coach) => {
        if (!isAnalyticsAllowed()) {
            return;
        }
        const identifier = buildIdentifier(user.type, user.id);
        amplitude.setUserId(identifier);
        const identifyEvent = new amplitude.Identify();
        identifyEvent.set("user_type", user.type);
        identifyEvent.set("user_email", user.email ?? "");
        identifyEvent.set("user_phone", user.phone_e164 ?? "");
        identifyEvent.set("first_name", user.first_name);
        identifyEvent.set("last_name", user.last_name);
        identifyEvent.set(
            "has_payment_setup",
            (user.type === "client" && user.has_payment_setup) ?? false,
        );
        amplitude.identify(identifyEvent);
    };

    const setMarketingUserProperties = () => {
        if (!isAnalyticsAllowed()) {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        const thm = params.get("thm");
        const h1tag = params.get("h1tag");
        if (!thm && !h1tag) {
            return;
        }
        const identifyEvent = new amplitude.Identify();
        if (thm) {
            identifyEvent.set("thm", thm);
        }
        // Remove this part if dynamic_homepage_h1_h2 fails
        if (h1tag) {
            identifyEvent.set("h1tag", h1tag);
        }
        amplitude.identify(identifyEvent);
    };

    const getExperimentOverrideCookie = (experimentName: string) => {
        return getCookie<string | null>(`e2e_exp_${experimentName}`, null);
    };

    const getE2EForcedDefaultVariant = () => {
        return getCookie<string | null>(E2E_FORCE_DEFAULT_VARIANT_COOKIE, null);
    };

    const getExperimentVariant = (
        experimentName: string,
        fallback: string = "control",
        triggerExposure: boolean = true,
    ) => {
        if (!experimentationInitialized && isAnalyticsAllowed()) {
            return undefined;
        }
        const experimentOverride = getExperimentOverrideCookie(experimentName);
        if (experimentOverride) {
            return experimentOverride;
        }
        const e2eForcedDefault = getE2EForcedDefaultVariant();
        if (e2eForcedDefault) {
            return e2eForcedDefault;
        }
        if (!isAnalyticsAllowed()) {
            return fallback;
        }
        const variantFromAmplitude =
            experimentation.variant(experimentName).value;
        const variant = variantFromAmplitude ?? fallback;
        if (variantFromAmplitude && triggerExposure) {
            experimentation.exposure(experimentName);
        }
        return variant;
    };

    const triggerExposure = (experimentName: string) => {
        if (!experimentationInitialized && isAnalyticsAllowed()) {
            return;
        }
        if (!isAnalyticsAllowed()) {
            return;
        }
        experimentation.exposure(experimentName);
    };

    const triggerExposureOnBackend = async (experimentName: string) => {
        await window.axios.post(route("expose"), {
            experimentName,
        });
    };

    const buildIdentifier = (userType: string, userId: number) => {
        let retVal = userType + "|" + userId;
        if (import.meta.env.VITE_LOCAL_ENVIRONMENT_ID) {
            retVal += "|" + import.meta.env.VITE_LOCAL_ENVIRONMENT_ID;
        }
        return retVal;
    };

    const isAnalyticsAllowed = () => {
        return cookieSettings.analytics;
    };

    const { user } = usePageProps();
    setMarketingUserProperties();
    if (user) {
        identifyUser(user);
    }

    useEffect(() => {
        (async () => {
            if (!isAnalyticsAllowed()) {
                optOut();
                setExperimentationInitialized(true);
                return;
            }
            amplitude.setOptOut(false);
            const sessionReplayTracking = sessionReplayPlugin();
            amplitude.add(sessionReplayTracking);
            amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
                autocapture: true,
                deviceId: getCookie("AMP_deviceId", undefined),
            });
            try {
                await experimentation.start();
            } catch (_e) {
                /**
                 * Experimentation failed to start. This is likely due to users running an ad or tracking blocker.
                 * We'll set experimentation initialization as completed, but users will always get the fallback variant.
                 */
            } finally {
                setExperimentationInitialized(true);
            }
        })();
    }, [isAnalyticsAllowed()]);

    return (
        <AnalyticsContext.Provider
            value={
                // If analytics are not allowed, we absolutely do not want to pass functions that actually do
                // anything to the children. This is why we return the initialization object.
                !isAnalyticsAllowed()
                    ? {
                          ...initialAnalyticsContext,
                          // We still want to be able to get experiment variants, so this function needs to manually
                          // check if analytics are allowed.
                          trackEventAttachedToClient,
                          getExperimentVariant,
                          experimentationInitialized,
                      }
                    : {
                          trackEvent,
                          trackEventAttachedToClient,
                          getExperimentVariant,
                          experimentationInitialized,
                          triggerExposure,
                          triggerExposureOnBackend,
                      }
            }
        >
            {children}
        </AnalyticsContext.Provider>
    );
}
